import type { ReactElement } from 'react';
import {
  BillingRegistryPlan as Plan,
  BillingRegistryPlanFrequency as Frequency
} from '@tryiris-ai/common.protojs/api/v1/billing_registry';
import type { PlanPurchaseView } from '#~/api/billing/plan-purchase.js';

// Stripe charge units differ from ISO display precision for MGA, ISK and UGX.
// https://docs.stripe.com/currencies#zero-decimal
const zeroDecimalCharges = new Set([
  'BIF',
  'CLP',
  'DJF',
  'GNF',
  'JPY',
  'KMF',
  'KRW',
  'MGA',
  'PYG',
  'RWF',
  'VND',
  'VUV',
  'XAF',
  'XOF',
  'XPF'
]);

export const futurePurchaseAmount = (
  amount: number,
  currency: string,
  locale?: string
): string | undefined => {
  if (!Number.isSafeInteger(amount) || amount < 0 || !/^[a-z]{3}$/i.test(currency))
    return undefined;
  const code = currency.toUpperCase();
  const zero = zeroDecimalCharges.has(code);
  const displayZero = zero || code === 'ISK' || code === 'UGX';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: code,
    minimumFractionDigits: displayZero ? 0 : 2,
    maximumFractionDigits: displayZero ? 0 : 2
  }).format(amount / (zero ? 1 : 100));
};

// Device-local time includes seconds and a timezone without storing a new preference.
export const futurePurchaseDate = (unix: number, locale?: string): string | undefined => {
  const date = new Date(unix * 1000);
  if (unix <= 0 || !Number.isFinite(date.getTime())) return undefined;
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'long' }).format(date);
};

const futureStatusMessage = (purchase: PlanPurchaseView, plan: string, start?: string): string => {
  switch (purchase.status) {
    case 'scheduled':
      return start === undefined
        ? `${plan} is scheduled. Checking its start time.`
        : `${plan} starts ${start}.`;
    case 'activating':
      return `Processing the first ${plan} payment. Paid access starts after payment is confirmed.`;
    case 'payment-required':
      return `The first ${plan} payment needs attention. Paid access has not started.`;
    case 'canceling':
      return 'Canceling the future purchase. Cancellation is not confirmed yet.';
    case 'canceled':
      return 'Future purchase canceled. No future subscription fee will be collected.';
    case 'failed':
      return 'The future purchase could not be completed. Paid access has not started.';
    case 'unavailable':
      return 'Purchase status is unavailable. Check the same purchase again.';
    case 'confirmed':
    case 'payment-received':
      return 'Payment confirmed. Refreshing this Studio’s access.';
    case 'open':
      return 'Card setup is open. The future purchase is not confirmed yet.';
    case 'expired':
      return 'That purchase is no longer active.';
    default:
      return 'Checking the future purchase. Your current access is unchanged.';
  }
};

/** Future consent and quoted fees never replace the separately loaded current plan. */
export const FuturePlanPurchaseSummary = ({
  purchase,
  currentPlan,
  currentTrialEnd
}: {
  readonly purchase: PlanPurchaseView;
  readonly currentPlan: Plan | undefined;
  readonly currentTrialEnd: number | undefined;
}): ReactElement => {
  const receipt = purchase.receipt;
  const plan =
    receipt?.plan === Plan.PLAN_PRO
      ? 'Pro'
      : receipt?.plan === Plan.PLAN_STANDARD
        ? 'Standard'
        : 'Selected plan';
  const end = futurePurchaseDate(currentTrialEnd ?? 0);
  const start = futurePurchaseDate(receipt?.effectiveAt ?? 0);
  return (
    <div className='flex flex-col gap-8' aria-live='polite'>
      <p>
        {currentPlan === Plan.PLAN_STANDARD && end !== undefined
          ? `Standard trial continues until ${end}.`
          : currentPlan === Plan.PLAN_FREE
            ? 'This Studio is currently on Free.'
            : currentPlan === Plan.PLAN_STANDARD || currentPlan === Plan.PLAN_PRO
              ? `This Studio currently has the ${currentPlan === Plan.PLAN_PRO ? 'Pro' : 'Standard'} plan.`
              : 'Checking this Studio’s current access.'}
      </p>
      <p>{futureStatusMessage(purchase, plan, start)}</p>
      <FuturePlanPurchaseQuote receipt={receipt} />
    </div>
  );
};

/** Confirmation and later status display use the same server-owned fee and calendar fields. */
export const FuturePlanPurchaseQuote = ({
  receipt
}: {
  readonly receipt: PlanPurchaseView['receipt'];
}): ReactElement => {
  const plan =
    receipt?.plan === Plan.PLAN_PRO
      ? 'Pro'
      : receipt?.plan === Plan.PLAN_STANDARD
        ? 'Standard'
        : 'Selected plan';
  const first =
    receipt === undefined
      ? undefined
      : futurePurchaseAmount(receipt.firstInvoiceAmount, receipt.currency);
  const recurring =
    receipt === undefined
      ? undefined
      : futurePurchaseAmount(receipt.recurringInvoiceAmount, receipt.currency);
  const period =
    receipt?.frequency === Frequency.PLAN_FREQUENCY_MONTHLY
      ? 'month'
      : receipt?.frequency === Frequency.PLAN_FREQUENCY_YEARLY
        ? 'year'
        : undefined;
  const quoted =
    receipt?.quoteId !== undefined &&
    receipt.quoteId !== '' &&
    first !== undefined &&
    recurring !== undefined &&
    period !== undefined;
  // A repeating promotion can outlast the first renewal; its end determines when the regular fee applies.
  const promotionEnd = futurePurchaseDate(receipt?.promotionEndAt ?? 0);
  return (
    <div className='flex flex-col gap-8'>
      {quoted ? (
        <>
          <p>
            Selected plan: {plan}. First subscription fee: {first}. Regular subscription fee:{' '}
            {recurring} per {period}
            {promotionEnd !== undefined ? ' after the promotion ends' : ''}.
          </p>
          {receipt?.promotionLabel && <p>Promotion: {receipt.promotionLabel}</p>}
          {promotionEnd !== undefined && <p>Promotion ends {promotionEnd}.</p>}
          {receipt?.firstRenewalAt !== undefined &&
            futurePurchaseDate(receipt.firstRenewalAt) !== undefined && (
              <p>First renewal: {futurePurchaseDate(receipt.firstRenewalAt)}.</p>
            )}
          <p className='text-sm text-muted'>
            Cloud usage is billed separately under this Studio’s cloud settings.
          </p>
        </>
      ) : (
        <p>
          Purchase details are unavailable. Check this purchase again before taking a billing
          action.
        </p>
      )}
    </div>
  );
};
